import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Check, School,
  MapPin, Phone, User, ChevronRight, Search
} from 'lucide-react';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const STATUS_BADGE = {
  active:   <span className="badge badge-green">Active</span>,
  inactive: <span className="badge badge-grey">Inactive</span>,
  prospect: <span className="badge badge-orange">Prospect</span>,
};

export default function SchoolsPage() {
  const { can } = useAuth();
  const [schools, setSchools]           = useState([]);
  const [districts, setDistricts]       = useState([]);
  const [pincodes, setPincodes]         = useState([]);
  const [filteredPincodes, setFilteredPincodes] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(false);
  const [editing, setEditing]           = useState(null);
  const [confirm, setConfirm]           = useState(null);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [detail, setDetail]             = useState(null);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const watchDistrict = watch('district_id');

  useEffect(() => {
    if (watchDistrict) {
      setFilteredPincodes(pincodes.filter(p => String(p.district_id) === String(watchDistrict)));
    } else {
      setFilteredPincodes(pincodes);
    }
  }, [watchDistrict, pincodes]);

  const loadMeta = useCallback(async () => {
    try {
      const [d, p, ay] = await Promise.all([
        api.get('/masters/districts?active=true'),
        api.get('/masters/pincodes?active=true'),
        api.get('/masters/academic-years'),
      ]);
      setDistricts(d.data.data);
      setPincodes(p.data.data);
      setFilteredPincodes(p.data.data);
      setAcademicYears(ay.data.data);
    } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search)         params.set('search', search);
      if (filterStatus)   params.set('status', filterStatus);
      if (filterDistrict) params.set('district_id', filterDistrict);
      const { data } = await api.get(`/schools?${params}`);
      setSchools(data.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterDistrict]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    const activeYear = academicYears.find(y => y.is_active);
    reset({
      school_name: '', district_id: '', pincode_id: '', address: '',
      academic_year_id: activeYear?.id || '',
      principal_name: '', principal_contact: '',
      spoc_name: '', spoc_contact: '',
      teacher_name: '', teacher_contact: '',
      status: 'prospect', notes: '',
    });
    setModal(true);
  };

  const openEdit = (school) => {
    setEditing(school);
    reset({ ...school });
    setModal(true);
  };

  const onSubmit = async (values) => {
    try {
      if (editing) { await api.put(`/schools/${editing.id}`, values); toast.success('School updated'); }
      else         { await api.post('/schools', values); toast.success('School created'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/schools/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  const openDetail = async (s) => {
    try {
      const { data } = await api.get(`/schools/${s.id}`);
      setDetail(data.data);
    } catch { toast.error('Failed to load'); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Schools</h1>
          <p className="page-subtitle">Onboard and manage schools across your territory</p>
        </div>
        {can('schools','create') && (
          <button className="btn btn-primary" onClick={openCreate}><Plus size={15} /> Add School</button>
        )}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-bar" style={{ flex: 1, minWidth: 220 }}>
              <Search size={14} />
              <input placeholder="Search school, principal, teacher…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="form-control" style={{ width: 150 }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="prospect">Prospect</option>
            </select>
            <select className="form-control" style={{ width: 200 }}
              value={filterDistrict} onChange={e => setFilterDistrict(e.target.value)}>
              <option value="">All Districts</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.district_name}</option>)}
            </select>
            <span className="badge badge-orange">{schools.length} schools</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>School Name</th>
                  <th>Location</th>
                  <th>Principal</th>
                  <th>Teacher</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schools.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--grey-800)' }}>{s.school_name}</div>
                      {s.year_label && <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{s.year_label}</div>}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{s.district_name || '—'}</div>
                      {s.pincode && <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{s.pincode} {s.area_name}</div>}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{s.principal_name || '—'}</div>
                      {s.principal_contact && (
                        <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{s.principal_contact}</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>{s.teacher_name || '—'}</div>
                      {s.teacher_contact && (
                        <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{s.teacher_contact}</div>
                      )}
                    </td>
                    <td>{STATUS_BADGE[s.status]}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => openDetail(s)}>
                          View <ChevronRight size={12} />
                        </button>
                        {can('schools','edit') && (
                          <button className="btn-icon" onClick={() => openEdit(s)}><Pencil size={14} /></button>
                        )}
                        {can('schools','delete') && (
                          <button className="btn-icon" onClick={() => setConfirm(s.id)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!schools.length && (
                  <tr><td colSpan={6}>
                    <div className="empty-state"><School size={36} /><h3>No schools found</h3><p>Add your first school to get started.</p></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── School Detail Drawer ─────────────────── */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><School size={18} /> {detail.school_name}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {can('schools','edit') && (
                  <button className="btn btn-sm btn-secondary"
                    onClick={() => { setDetail(null); openEdit(detail); }}>
                    <Pencil size={12} /> Edit
                  </button>
                )}
                <button className="btn-icon" onClick={() => setDetail(null)}><X size={18} /></button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <InfoBlock icon={<MapPin size={14}/>} label="Location">
                  <div>{detail.district_name}{detail.state_name ? `, ${detail.state_name}` : ''}</div>
                  {detail.pincode && <div style={{ fontSize: 12, color: 'var(--grey-400)' }}>{detail.pincode} — {detail.area_name}</div>}
                  {detail.address && <div style={{ fontSize: 12, color: 'var(--grey-500)', marginTop: 4 }}>{detail.address}</div>}
                </InfoBlock>
                <InfoBlock icon={<User size={14}/>} label="Principal">
                  <div>{detail.principal_name || '—'}</div>
                  {detail.principal_contact && <div style={{ fontSize: 12, color: 'var(--grey-400)' }}><Phone size={10}/> {detail.principal_contact}</div>}
                </InfoBlock>
                <InfoBlock icon={<User size={14}/>} label="SPOC">
                  <div>{detail.spoc_name || '—'}</div>
                  {detail.spoc_contact && <div style={{ fontSize: 12, color: 'var(--grey-400)' }}><Phone size={10}/> {detail.spoc_contact}</div>}
                </InfoBlock>
                <InfoBlock icon={<User size={14}/>} label="Music Teacher">
                  <div>{detail.teacher_name || '—'}</div>
                  {detail.teacher_contact && <div style={{ fontSize: 12, color: 'var(--grey-400)' }}><Phone size={10}/> {detail.teacher_contact}</div>}
                </InfoBlock>
              </div>

              <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
                  Recent Orders
                </div>
                {detail.recent_orders?.length ? (
                  <table className="data-table" style={{ fontSize: 12 }}>
                    <thead><tr><th>Ref</th><th>Batch</th><th>Tutorial</th><th>Qty</th><th>Status</th></tr></thead>
                    <tbody>
                      {detail.recent_orders.map(o => (
                        <tr key={o.id}>
                          <td><code style={{ fontSize: 11 }}>{o.order_ref}</code></td>
                          <td>{o.batchName}</td>
                          <td>{o.tutorial_name}</td>
                          <td>{o.quantity}</td>
                          <td><StatusBadge status={o.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ fontSize: 12, color: 'var(--grey-400)' }}>No orders yet for this school.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add/Edit Modal ───────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} School</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                {/* School Info */}
                <SectionLabel>School Information</SectionLabel>
                <div className="form-group">
                  <label className="form-label">School Name <span className="req">*</span></label>
                  <input className={`form-control ${errors.school_name ? 'error' : ''}`}
                    placeholder="e.g. Little Kingdom Public School"
                    {...register('school_name', { required: 'Required' })} />
                  {errors.school_name && <p className="form-error">{errors.school_name.message}</p>}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">District</label>
                    <select className="form-control" {...register('district_id')}>
                      <option value="">Select district…</option>
                      {districts.map(d => <option key={d.id} value={d.id}>{d.district_name} — {d.state_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <select className="form-control" {...register('pincode_id')}>
                      <option value="">Select pincode…</option>
                      {filteredPincodes.map(p => <option key={p.id} value={p.id}>{p.pincode} {p.area_name ? `— ${p.area_name}` : ''}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Academic Year</label>
                    <select className="form-control" {...register('academic_year_id')}>
                      <option value="">Select year…</option>
                      {academicYears.map(ay => <option key={ay.id} value={ay.id}>{ay.year_label}{ay.is_active ? ' ✓' : ''}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" {...register('status')}>
                      <option value="prospect">Prospect</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <textarea className="form-control" rows={2} placeholder="Full school address" {...register('address')} />
                </div>

                {/* Contacts */}
                <SectionLabel>Contact Details</SectionLabel>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Principal Name</label>
                    <input className="form-control" placeholder="Principal full name" {...register('principal_name')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Principal Contact</label>
                    <input className="form-control" placeholder="Mobile number" {...register('principal_contact')} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">SPOC Name</label>
                    <input className="form-control" placeholder="School Point of Contact" {...register('spoc_name')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SPOC Contact</label>
                    <input className="form-control" placeholder="Mobile number" {...register('spoc_contact')} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Music Teacher Name</label>
                    <input className="form-control" placeholder="Teacher name" {...register('teacher_name')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teacher Contact</label>
                    <input className="form-control" placeholder="Mobile number" {...register('teacher_contact')} />
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label className="form-label">Internal Notes</label>
                  <textarea className="form-control" rows={2} placeholder="Any internal remarks…" {...register('notes')} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14} /> Save School</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!confirm} title="Delete School"
        message="Schools with orders cannot be deleted."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: 'var(--orange-600)',
    textTransform: 'uppercase', letterSpacing: '.07em',
    marginBottom: 12, marginTop: 4,
    paddingBottom: 6, borderBottom: '1px solid var(--orange-100)'
  }}>
    {children}
  </div>
);

const InfoBlock = ({ icon, label, children }) => (
  <div style={{ background: 'var(--grey-50)', borderRadius: 8, padding: '12px 14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 13, color: 'var(--grey-700)' }}>{children}</div>
  </div>
);

const STATUS_COLORS = {
  draft:            { bg: '#f3f4f6', color: '#374151' },
  quote_raised:     { bg: '#fef3c7', color: '#92400e' },
  approved:         { bg: '#dbeafe', color: '#1e40af' },
  codes_generated:  { bg: '#dcfce7', color: '#166534' },
  completed:        { bg: '#d1fae5', color: '#065f46' },
  cancelled:        { bg: '#fee2e2', color: '#991b1b' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 600,
      padding: '2px 8px', borderRadius: 10,
      display: 'inline-block', textTransform: 'capitalize',
    }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};
